import { api } from "../api.ts";
import { findCreator, getCatalogue } from "../catalogue.ts";
import { gameGrid } from "../components.ts";
import { clear, h, link, toast } from "../dom.ts";
import { navigate, setTitle } from "../router.ts";
import { currentMe, signedIn } from "../session.ts";
import { ownedAssets, store } from "../store-client.ts";
import { creatorStorePath } from "../store.ts";
import { assetGrid } from "./store.ts";
import { selling, visibleItems } from "../selling.ts";

interface Library {
  library: string[];
  hearts: string[];
  follows: { games: string[]; creators: string[] };
}

/** A follow is an accounts mark `{kind: "follow", target: "cr_…"}`; last write wins, so a client timestamp is enough. */
export function followMark(creatorId: string, on: boolean, now = Date.now()) {
  return { kind: "follow" as const, target: creatorId, on, updatedAt: now };
}

export async function isFollowing(creatorId: string): Promise<boolean> {
  if (!signedIn()) return false;
  const lib = await api<Library>("accounts", "/me/library");
  return lib.follows.creators.includes(creatorId);
}

export async function setFollow(creatorId: string, on: boolean): Promise<void> {
  await api("accounts", "/me/sync", { body: { marks: [followMark(creatorId, on)], since: 0 } });
}

export function followButton(creatorId: string, name: string): HTMLElement {
  let on = false;
  const btn = h("button", { type: "button", class: "btn", "aria-pressed": "false" }, "Follow");
  const paint = () => {
    btn.textContent = on ? "Following" : "Follow";
    btn.setAttribute("aria-pressed", on ? "true" : "false");
    btn.classList.toggle("ghost", on);
  };
  btn.addEventListener("click", async () => {
    if (!signedIn()) return navigate(`/signin?next=${encodeURIComponent(location.pathname)}`);
    btn.setAttribute("disabled", "");
    try {
      await setFollow(creatorId, !on);
      on = !on;
      paint();
      toast(on ? `Following ${name}. Their new games and devlogs reach you in the Banger app.` : `Unfollowed ${name}`);
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e));
    } finally {
      btn.removeAttribute("disabled");
    }
  });
  void isFollowing(creatorId)
    .then((v) => ((on = v), paint()))
    .catch(() => {});
  return btn;
}

export async function creatorPage(params: Record<string, string>): Promise<HTMLElement> {
  const cat = await getCatalogue();
  // creators with only a handle (no game yet) aren't in the catalogue's list
  const found = await findCreator(params.id ?? "");
  if (!found) throw new Error("No creator by that name on Banger.");
  const c = found.creator;
  setTitle(c.name);
  const games = cat.games.filter((g) => g.creator.id === c.id);
  let site: string | null = null;
  try {
    const u = c.url ? new URL(c.url) : null;
    site = u && (u.protocol === "https:" || u.protocol === "http:") ? u.href : null;
  } catch {
    site = null;
  }
  return h(
    "article",
    { class: "creator" },
    h(
      "header",
      { class: "creator-head" },
      h("div", { class: "avatar-badge", "aria-hidden": "true" }, c.name.slice(0, 1).toUpperCase()),
      h(
        "div",
        null,
        h("h1", null, c.name),
        h(
          "p",
          { class: "dim" },
          games.length ? `${games.length} ${games.length === 1 ? "game" : "games"}` : "Creator on Banger",
          site ? [" · ", h("a", { href: site, rel: "noopener noreferrer", target: "_blank" }, new URL(site).host)] : null,
          c.claimedBy ? " · Verified creator" : null,
        ),
      ),
      followButton(c.id, c.name),
    ),
    games.length ? h("section", { class: "section" }, h("h2", null, "Games"), gameGrid(games)) : null,
    creatorAssets(c.id, games.length === 0),
    c.claimedBy ? null : h("p", { class: "dim small" }, "Made these? ", link("/dashboard", null, "Claim this creator page"), " to post devlogs, answer bug reports and see your players."),
  );
}

/**
 * The creator's store assets (a store handle publishes under cr_<handle>). With none: nothing for a creator with
 * games, and "No assets yet" for a handle-only creator, whose page would otherwise be empty.
 */
function creatorAssets(creatorId: string, sayEmpty: boolean): HTMLElement | null {
  if (!creatorId.startsWith("cr_")) return null;
  const empty = h("section", { class: "section" }, h("h2", null, "Assets"), h("p", { class: "dim" }, "No assets yet."));
  const el = sayEmpty ? empty : h("section", { class: "section", hidden: true });
  void (async () => {
    const b = await store();
    const c = await b.creator(creatorId.slice(3));
    const shown = c ? visibleItems(c.assets, selling()) : []; // paid items leave the lists without selling (wave 9)
    if (!c || !shown.length) return;
    const owned = await ownedAssets(currentMe()?.id ?? null);
    clear(el, h("div", { class: "section-head" }, h("h2", null, "Assets"), link(creatorStorePath(c.handle), { class: "more" }, "All assets")), assetGrid(shown.slice(0, 8), owned));
    el.hidden = false;
  })().catch(() => {});
  return el;
}
